import AppContext from 'App/AppContext';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { useContext, useEffect, useRef, useState } from 'react';
import { apiGetLinkWriteAccess } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import { getDocumentViewMode } from './copilotDocumentBlockHelpers';

export interface CopilotDocumentWriteAccess {
  // False only when write access was positively denied by the backend; true while editing is allowed or the
  // check does not apply (the Edit button uses this together with isCheckingAccess).
  canEdit: boolean;
  // True while the write-access check is in flight, so the Edit button can be disabled during that window.
  isCheckingAccess: boolean;
}

// Shared write-access primitive for a library-backed document, keyed by its SharePoint link id. requiresCheck
// gates the call: pass false when the document is not an editable type or the consuming surface is hidden, and
// the hook reports canEdit=true without hitting the backend. Results are memoized per link id so navigating
// between versions/documents does not re-hit the backend. Fails open: any error - including the probe's own
// timeout (it is time-boxed in apiGetLinkWriteAccess) - leaves editing enabled; unknown means allowed, and the
// editor still surfaces a real save failure if the user cannot write.
export const useLinkWriteAccess = (
  linkId: number | undefined,
  requiresCheck: boolean,
): CopilotDocumentWriteAccess => {
  const appContext = useContext(AppContext);
  const [canEdit, setCanEdit] = useState<boolean>(true);
  const [isCheckingAccess, setIsCheckingAccess] = useState<boolean>(false);
  const cacheRef = useRef<Map<number, boolean>>(new Map());

  const shouldCheck = requiresCheck && !!linkId;

  useEffect(() => {
    if (!shouldCheck || !linkId) {
      setCanEdit(true);
      setIsCheckingAccess(false);

      return;
    }

    const cached = cacheRef.current.get(linkId);
    if (cached !== undefined) {
      setCanEdit(cached);
      setIsCheckingAccess(false);

      return;
    }

    let cancelled = false;
    setIsCheckingAccess(true);

    const check = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const result = await apiGetLinkWriteAccess(linkId, accessToken);
        if (cancelled) return;

        cacheRef.current.set(linkId, result);
        setCanEdit(result);
      } catch (err) {
        // Fail open: a failed check must not block a user who may well have write access.
        logger.debug('useLinkWriteAccess: write-access check failed, leaving Edit enabled', err);
        if (!cancelled) setCanEdit(true);
      } finally {
        if (!cancelled) setIsCheckingAccess(false);
      }
    };

    void check();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId, shouldCheck]);

  return { canEdit, isCheckingAccess };
};

// Resolves whether the current user may edit the SharePoint item behind a previewed Copilot library document
// (docx drive item or SharePoint page). Only library-backed docx/page blocks are checked; any other block is
// treated as editable (the gate does not apply). Thin block-facing wrapper over useLinkWriteAccess.
export const useCopilotDocumentWriteAccess = (
  block: CopilotConversationContentBlock | undefined,
): CopilotDocumentWriteAccess => {
  const mode = getDocumentViewMode(block);

  return useLinkWriteAccess(block?.savedLibraryLinkId, mode === 'docx' || mode === 'page');
};
